import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { B3_TOKEN } from "../../../anyspend/index.js";
import { Button, GlareCardRounded, Input, StyleRoot, TextLoop, toast, useHasMounted, useModalStore, useTokenBalance, useUnifiedChainSwitchAndExecute } from "../../../global-account/react/index.js";
import { PUBLIC_BASE_RPC_URL } from "../../../shared/constants/index.js";
import { baseMainnet } from "../../../shared/constants/chains/supported.js";
import invariant from "invariant";
import { ArrowRight, Loader2 } from "lucide-react";
import { motion } from "motion/react";
import { useCallback, useEffect, useState } from "react";
import { createPublicClient, encodeFunctionData, erc20Abi, formatUnits, http } from "viem";
import { useAccount, useWaitForTransactionReceipt } from "wagmi";
import { AnySpendCustom } from "./AnySpendCustom.js";
import { EthIcon } from "./icons/EthIcon.js";
import { SolIcon } from "./icons/SolIcon.js";
import { UsdcIcon } from "./icons/USDCIcon.js";
const SPIN_WHEEL_ABI = [{
  inputs: [],
  name: "getPaymentConfig",
  outputs: [{
    internalType: "address",
    name: "paymentToken",
    type: "address"
  }, {
    internalType: "uint256",
    name: "pricePerEntry",
    type: "uint256"
  }, {
    internalType: "uint256",
    name: "maxEntriesPerUser",
    type: "uint256"
  }, {
    internalType: "address",
    name: "paymentRecipient",
    type: "address"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  name: "entryModule",
  outputs: [{
    internalType: "contract IEntryModuleV2",
    name: "",
    type: "address"
  }],
  stateMutability: "view",
  type: "function"
}, {
  inputs: [{
    internalType: "address",
    name: "user",
    type: "address"
  }, {
    internalType: "uint256",
    name: "quantity",
    type: "uint256"
  }],
  name: "buyEntriesAndSpin",
  outputs: [],
  stateMutability: "payable",
  type: "function"
}, {
  inputs: [],
  name: "getWheelInfo",
  outputs: [{
    internalType: "address",
    name: "creator_",
    type: "address"
  }, {
    internalType: "uint256",
    name: "startTime_",
    type: "uint256"
  }, {
    internalType: "uint256",
    name: "endTime_",
    type: "uint256"
  }, {
    internalType: "uint256",
    name: "totalPrizesAvailable_",
    type: "uint256"
  }, {
    internalType: "uint256",
    name: "prizesRequestedCount_",
    type: "uint256"
  }, {
    internalType: "enum SpinWheelV2.WheelState",
    name: "state_",
    type: "uint8"
  }],
  stateMutability: "view",
  type: "function"
}];
function getWheelStatus(wheelInfo) {
  const now = BigInt(Math.floor(Date.now() / 1000));
  if (now < wheelInfo.startTime_) {
    return "not_started";
  }
  if (now > wheelInfo.endTime_) {
    return "ended";
  }
  if (wheelInfo.totalPrizesAvailable_ <= wheelInfo.prizesRequestedCount_) {
    return "sold_out";
  }
  return "active";
}
function generateEncodedDataForBuyEntriesAndSpin(user, quantity) {
  invariant(BigInt(quantity) > 0, "Quantity must be greater than zero");
  const encodedData = encodeFunctionData({
    abi: SPIN_WHEEL_ABI,
    functionName: "buyEntriesAndSpin",
    args: [user, BigInt(quantity)]
  });
  return encodedData;
}
const basePublicClient = createPublicClient({
  chain: baseMainnet,
  transport: http(PUBLIC_BASE_RPC_URL)
});
export function AnySpendBuySpin({
  loadOrder,
  mode = "modal",
  spinwheelContractAddress,
  chainId,
  recipientAddress,
  prefillQuantity,
  onSuccess,
  senderAddress,
  slots,
  content,
  theme,
  showFiatOption
}) {
  const hasMounted = useHasMounted();
  const {
    setB3ModalOpen
  } = useModalStore();
  // Payment config state
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [configError, setConfigError] = useState("");
  const [wheelInfo, setWheelInfo] = useState(null);
  // Fetch B3 token balance
  const {
    formattedBalance: b3Balance,
    isLoading: isBalanceLoading,
    rawBalance: b3RawBalance
  } = useTokenBalance({
    token: B3_TOKEN
  });
  // Wagmi hooks
  const {
    address
  } = useAccount();
  const {
    switchChainAndExecute,
    isSwitchingOrExecuting
  } = useUnifiedChainSwitchAndExecute();
  // State for direct buying flow (when user has B3 tokens)
  const [isBuying, setIsBuying] = useState(false);
  const [buyingTxHash, setBuyingTxHash] = useState("");
  const {
    isLoading: isTxPending,
    isSuccess: isTxSuccess,
    isError: isTxError,
    error: txError
  } = useWaitForTransactionReceipt({
    hash: buyingTxHash,
    query: {
      structuralSharing: false
    }
  });
  // Handle transaction status
  useEffect(() => {
    if (!buyingTxHash) return;
    if (isTxSuccess) {
      setB3ModalOpen(false);
      onSuccess?.(buyingTxHash);
      toast.success("Spin purchase transaction confirmed!");
      setIsBuying(false);
    } else if (isTxError) {
      console.error("@@anyspend-buy-spin:tx-error:", txError);
      toast.error("Transaction failed. Please try again.");
      setB3ModalOpen(false);
      setIsBuying(false);
    }
  }, [isTxSuccess, isTxError, buyingTxHash, onSuccess, setB3ModalOpen, txError]);
  // Spin quantity state
  const [userSpinQuantity, setUserSpinQuantity] = useState("");
  const [showAmountPrompt, setShowAmountPrompt] = useState(true);
  const [isQuantityValid, setIsQuantityValid] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [displayQuantity, setDisplayQuantity] = useState("");
  const [debouncedQuantity, setDebouncedQuantity] = useState("");
  const validateAndSetQuantity = useCallback(value => {
    // Only allow positive integers
    const isValidFormat = /^\d+$/.test(value) || value === "";
    if (!isValidFormat && value !== "") {
      return;
    }
    setDisplayQuantity(value);
    try {
      if (value === "") {
        setUserSpinQuantity("");
        setIsQuantityValid(false);
        setValidationError("");
        return;
      }
      const numValue = parseInt(value);
      if (isNaN(numValue) || numValue <= 0) {
        setIsQuantityValid(false);
        setUserSpinQuantity("");
        setValidationError("Please enter a valid positive number");
        return;
      }
      // Check maximum entries per user (0 means no limit)
      if (paymentConfig && paymentConfig.maxEntriesPerUser > BigInt(0) && BigInt(numValue) > paymentConfig.maxEntriesPerUser) {
        setIsQuantityValid(false);
        setUserSpinQuantity("");
        setValidationError(`Maximum ${paymentConfig.maxEntriesPerUser.toString()} spins allowed`);
        return;
      }
      // Check if quantity exceeds remaining entries
      if (wheelInfo && BigInt(numValue) > wheelInfo.totalPrizesAvailable_ - wheelInfo.prizesRequestedCount_) {
        setIsQuantityValid(false);
        setUserSpinQuantity("");
        setValidationError(`Only ${(wheelInfo.totalPrizesAvailable_ - wheelInfo.prizesRequestedCount_).toString()} spins remaining`);
        return;
      }
      setUserSpinQuantity(value);
      setIsQuantityValid(true);
      setValidationError("");
    } catch (error) {
      setIsQuantityValid(false);
      setUserSpinQuantity("");
      setValidationError("Please enter a valid quantity");
    }
  }, [paymentConfig, wheelInfo]);
  useEffect(() => {
    if (prefillQuantity && wheelInfo) {
      const remainingSpins = wheelInfo.totalPrizesAvailable_ - wheelInfo.prizesRequestedCount_;
      const adjustedQuantity = BigInt(prefillQuantity) > remainingSpins ? remainingSpins.toString() : prefillQuantity;
      validateAndSetQuantity(adjustedQuantity);
    }
  }, [prefillQuantity, validateAndSetQuantity, wheelInfo]);
  // Calculate total cost
  const totalCost = paymentConfig && userSpinQuantity ? paymentConfig.pricePerEntry * BigInt(userSpinQuantity) : BigInt(0);
  // Fetch payment configuration and wheel info
  const fetchPaymentConfig = useCallback(async () => {
    if (!basePublicClient || !spinwheelContractAddress) return;
    try {
      setIsLoadingConfig(true);
      setConfigError("");
      const [config, entryModuleAddress, wheelInfo_0] = await Promise.all([basePublicClient.readContract({
        address: spinwheelContractAddress,
        abi: SPIN_WHEEL_ABI,
        functionName: "getPaymentConfig"
      }), basePublicClient.readContract({
        address: spinwheelContractAddress,
        abi: SPIN_WHEEL_ABI,
        functionName: "entryModule"
      }), basePublicClient.readContract({
        address: spinwheelContractAddress,
        abi: SPIN_WHEEL_ABI,
        functionName: "getWheelInfo"
      })]);
      const paymentConfig_0 = {
        pricePerEntry: config[1],
        maxEntriesPerUser: config[2],
        paymentRecipient: config[3],
        entryModule: entryModuleAddress
      };
      const wheelInfoData = {
        creator_: wheelInfo_0[0],
        startTime_: wheelInfo_0[1],
        endTime_: wheelInfo_0[2],
        totalPrizesAvailable_: wheelInfo_0[3],
        prizesRequestedCount_: wheelInfo_0[4],
        state_: wheelInfo_0[5]
      };
      setPaymentConfig(paymentConfig_0);
      setWheelInfo(wheelInfoData);
    } catch (error_0) {
      console.error("@@anyspend-buy-spin:config-error:", error_0);
      setConfigError("Failed to load spin wheel configuration");
      toast.error("Failed to load spin wheel configuration");
    } finally {
      setIsLoadingConfig(false);
    }
  }, [spinwheelContractAddress]);
  // Fetch config on mount and when dependencies change
  useEffect(() => {
    fetchPaymentConfig();
  }, [fetchPaymentConfig]);
  // Debounce the quantity for balance checks
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuantity(displayQuantity);
    }, 500);
    return () => clearTimeout(timer);
  }, [displayQuantity, userSpinQuantity]);
  const handleDirectBuying = async () => {
    if (!address || !basePublicClient || !userSpinQuantity || !paymentConfig) return;
    try {
      setIsBuying(true);
      // Check current allowance for B3 token to entryModule
      const allowance = await basePublicClient.readContract({
        address: B3_TOKEN.address,
        abi: erc20Abi,
        functionName: "allowance",
        args: [address, paymentConfig.entryModule]
      });
      // If allowance is insufficient, request approval first
      if (allowance < totalCost) {
        toast.info("Approving B3 spending...");
        const approvalData = encodeFunctionData({
          abi: erc20Abi,
          functionName: "approve",
          args: [paymentConfig.entryModule, totalCost]
        });
        await switchChainAndExecute(chainId, {
          to: B3_TOKEN.address,
          data: approvalData,
          value: BigInt(0)
        });
        toast.info("Approval confirmed. Proceeding with spin purchase...");
      }
      // Execute the buy entries and spin
      toast.info("Buying spins...");
      const buyData = encodeFunctionData({
        abi: SPIN_WHEEL_ABI,
        functionName: "buyEntriesAndSpin",
        args: [address, BigInt(userSpinQuantity)]
      });
      const buyHash = await switchChainAndExecute(chainId, {
        to: spinwheelContractAddress,
        data: buyData,
        value: BigInt(0)
      });
      if (buyHash) {
        setBuyingTxHash(buyHash);
        toast.success("Spin purchase transaction submitted!");
      }
    } catch (error_1) {
      console.error("@@anyspend-buy-spin:error:", error_1);
      toast.error("Spin purchase failed. Please try again.");
      setB3ModalOpen(false);
    } finally {
      setIsBuying(false);
    }
  };
  const confirmQuantity = () => {
    if (!isQuantityValid || !paymentConfig) {
      toast.error("Please enter a valid quantity to buy");
      return;
    }
    // Check if user has sufficient B3 balance for direct buying
    const hasEnoughBalance = b3RawBalance && totalCost <= b3RawBalance;
    if (hasEnoughBalance) {
      // User has enough B3, proceed with direct buying
      handleDirectBuying();
    } else {
      // User needs more B3, proceed to AnySpend conversion flow
      setShowAmountPrompt(false);
    }
  };
  const header = () => _jsxs(_Fragment, {
    children: [_jsxs("div", {
      className: "relative mx-auto size-32",
      children: [_jsx("div", {
        className: "absolute inset-0 scale-95 rounded-[50%] bg-gradient-to-br from-blue-500/20 to-purple-600/20 blur-xl"
      }), _jsxs(GlareCardRounded, {
        className: "overflow-hidden rounded-full border-none bg-gradient-to-br from-blue-500/10 to-purple-600/10 backdrop-blur-sm",
        children: [_jsx("img", {
          alt: "B3 Token",
          className: "size-full",
          src: "https://cdn.b3.fun/b3-coin-3d.png"
        }), _jsx("div", {
          className: "absolute inset-0 rounded-[50%] border border-white/20"
        })]
      })]
    }), _jsxs("div", {
      className: "from-b3-react-background to-as-on-surface-1 mt-[-60px] w-full rounded-t-lg bg-gradient-to-t",
      children: [_jsx("div", {
        className: "h-[60px] w-full"
      }), _jsxs("div", {
        className: "mb-1 flex w-full flex-col items-center gap-2 p-5",
        children: [_jsxs("span", {
          className: "font-sf-rounded text-2xl font-semibold",
          children: ["Buy ", userSpinQuantity || "", " Spin", userSpinQuantity !== "1" ? "s" : ""]
        }), _jsx("p", {
          className: "text-as-primary/70 text-sm",
          children: "Pay with B3 \u2022 Get spin wheel entries"
        })]
      })]
    })]
  });
  const onFocusQuantityInput = () => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
  };
  // Loading state
  if (isLoadingConfig) {
    return _jsx(StyleRoot, {
      children: _jsxs("div", {
        className: "bg-b3-react-background flex w-full flex-col items-center justify-center p-8",
        children: [_jsx(Loader2, {
          className: "text-as-primary/70 h-8 w-8 animate-spin"
        }), _jsx("p", {
          className: "text-as-primary/70 mt-2 text-sm",
          children: "Loading spin wheel configuration..."
        })]
      })
    });
  }
  // Error state
  if (configError || !paymentConfig) {
    return _jsx(StyleRoot, {
      children: _jsxs("div", {
        className: "bg-b3-react-background flex w-full flex-col items-center justify-center p-8",
        children: [_jsx("p", {
          className: "text-as-red mb-4 text-center",
          children: configError || "Failed to load configuration"
        }), _jsx(Button, {
          onClick: fetchPaymentConfig,
          variant: "outline",
          children: "Retry"
        })]
      })
    });
  }
  // Render quantity input prompt
  if (showAmountPrompt) {
    const pricePerEntry = formatUnits(paymentConfig.pricePerEntry, 18);
    const remainingEntries = wheelInfo ? wheelInfo.totalPrizesAvailable_ - wheelInfo.prizesRequestedCount_ : BigInt(0);
    const wheelStatus = wheelInfo ? getWheelStatus(wheelInfo) : null;
    const isActive = wheelStatus === "active";
    const getStatusMessage = () => {
      if (!wheelInfo) return null;
      const formatDate = timestamp => {
        return new Date(Number(timestamp) * 1000).toLocaleString();
      };
      switch (wheelStatus) {
        case "not_started":
          return {
            title: "Spin Wheel Not Started",
            message: `Starts at ${formatDate(wheelInfo.startTime_)}`
          };
        case "ended":
          return {
            title: "Spin Wheel Ended",
            message: `Ended at ${formatDate(wheelInfo.endTime_)}`
          };
        case "sold_out":
          return {
            title: "All Spins Have Been Claimed",
            message: "Stay tuned for the next spin wheel event!"
          };
        default:
          return null;
      }
    };
    const statusInfo = getStatusMessage();
    return _jsx(StyleRoot, {
      children: _jsxs("div", {
        className: "bg-b3-react-background flex w-full flex-col items-center",
        children: [_jsxs("div", {
          className: "w-full px-4 pb-2 pt-4",
          children: [_jsx(motion.div, {
            initial: false,
            animate: {
              opacity: hasMounted ? 1 : 0,
              y: hasMounted ? 0 : 20,
              filter: hasMounted ? "blur(0px)" : "blur(10px)"
            },
            transition: {
              duration: 0.3,
              delay: 0,
              ease: "easeInOut"
            },
            className: `flex justify-center ${isActive ? "mb-4" : ""}`,
            children: _jsx("img", {
              alt: "B3 Token",
              loading: "lazy",
              width: "64",
              height: "64",
              decoding: "async",
              className: "rounded-full",
              src: "https://cdn.b3.fun/b3-coin-3d.png"
            })
          }), _jsx(motion.div, {
            initial: false,
            animate: {
              opacity: hasMounted ? 1 : 0,
              y: hasMounted ? 0 : 20,
              filter: hasMounted ? "blur(0px)" : "blur(10px)"
            },
            transition: {
              duration: 0.3,
              delay: 0.1,
              ease: "easeInOut"
            },
            className: "text-center",
            children: isActive ? _jsxs(_Fragment, {
              children: [_jsx("h2", {
                className: "font-sf-rounded text-as-primary mb-4 text-2xl font-bold",
                children: (() => {
                  const hasEnoughBalance_0 = b3RawBalance && totalCost <= b3RawBalance;
                  return hasEnoughBalance_0 || !debouncedQuantity ? "Buy Spins" : `Swap & Buy Spins`;
                })()
              }), wheelInfo && _jsxs("div", {
                className: "inline-flex items-center gap-2",
                children: [_jsx("div", {
                  className: "bg-as-brand/10 border-as-brand/10 inline-flex items-center rounded-full border px-3 py-1",
                  children: _jsxs("p", {
                    className: "text-as-brand text-sm font-medium",
                    children: [pricePerEntry, " $B3 per spin"]
                  })
                }), _jsx("div", {
                  className: "bg-as-brand/10 border-as-brand/10 inline-flex items-center rounded-full border px-3 py-1",
                  children: _jsxs("p", {
                    className: "text-as-brand text-sm font-medium",
                    children: [remainingEntries.toString(), " remaining"]
                  })
                })]
              })]
            }) : statusInfo && _jsxs("div", {
              className: "text-center",
              children: [_jsx("p", {
                className: "text-as-primary text-lg font-semibold",
                children: statusInfo.title
              }), _jsx("p", {
                className: "text-as-primary/70 mt-2 text-sm",
                children: statusInfo.message
              })]
            })
          })]
        }), _jsx(motion.div, {
          initial: false,
          animate: {
            opacity: hasMounted ? 1 : 0,
            y: hasMounted ? 0 : 20,
            filter: hasMounted ? "blur(0px)" : "blur(10px)"
          },
          transition: {
            duration: 0.3,
            delay: 0.2,
            ease: "easeInOut"
          },
          className: "bg-b3-react-background w-full p-6",
          children: isActive ? _jsxs("div", {
            className: "space-y-4",
            children: [_jsxs("div", {
              className: "flex items-center justify-between",
              children: [_jsx("p", {
                className: "text-as-primary/70 text-sm font-medium",
                children: "Number of spins"
              }), _jsxs("span", {
                className: "text-as-primary/50 flex items-center gap-1 text-sm",
                children: ["Available: ", isBalanceLoading ? _jsx(Loader2, {
                  className: "h-3 w-3 animate-spin"
                }) : `${b3Balance} B3`]
              })]
            }), _jsxs("div", {
              className: "relative",
              children: [_jsx(Input, {
                onFocus: onFocusQuantityInput,
                type: "text",
                placeholder: "1",
                value: displayQuantity,
                onChange: e => validateAndSetQuantity(e.target.value),
                className: `h-14 px-4 pr-20 text-lg ${!isQuantityValid && displayQuantity ? "border-as-red" : "border-b3-react-border"}`
              }), _jsx("div", {
                className: "font-pack absolute right-4 top-1/2 -translate-y-1/2 text-lg font-medium text-blue-500/70",
                children: displayQuantity === "1" ? "Spin" : "Spins"
              })]
            }), !isQuantityValid && displayQuantity && _jsx("p", {
              className: "text-as-red text-sm",
              children: validationError
            }), _jsx("div", {
              className: "bg-as-on-surface-2/30 rounded-lg border border-white/10 p-4 backdrop-blur-sm",
              children: _jsxs("div", {
                className: "flex items-center justify-between",
                children: [_jsx("span", {
                  className: "text-as-primary/70 text-sm font-medium",
                  children: "Total Cost:"
                }), _jsx("div", {
                  className: "flex items-center gap-2",
                  children: _jsxs("span", {
                    className: "text-as-primary text-lg font-bold",
                    children: [displayQuantity && isQuantityValid ? formatUnits(totalCost, 18) : "0", " B3"]
                  })
                })]
              })
            }), _jsx("div", {
              className: "mt-4",
              children: (() => {
                const hasEnoughBalance_1 = b3RawBalance && totalCost <= b3RawBalance;
                if (!hasEnoughBalance_1 && debouncedQuantity) {
                  return _jsxs("div", {
                    className: "bg-as-brand/10 flex flex-col items-center gap-2 rounded-lg p-4 pb-5",
                    children: [_jsxs("div", {
                      className: "flex items-center justify-center gap-2",
                      children: [_jsx("span", {
                        className: "text-as-primary text-sm font-semibold",
                        children: "Swap & buy from any token"
                      }), _jsxs(TextLoop, {
                        children: [_jsx(EthIcon, {
                          className: "h-8 w-8"
                        }), _jsx(SolIcon, {
                          className: "h-8 w-8"
                        }), _jsx(UsdcIcon, {
                          className: "h-8 w-8"
                        })]
                      }), _jsx(ArrowRight, {
                        className: "text-as-primary h-4 w-4"
                      }), _jsx("img", {
                        src: "https://cdn.b3.fun/b3-coin-3d.png",
                        className: "h-7 w-7",
                        alt: "B3 Token"
                      })]
                    }), _jsx("p", {
                      className: "text-as-primary/50 text-sm font-medium",
                      children: "No problem, we'll help you swap to B3 for your spins!"
                    })]
                  });
                }
              })()
            }), _jsx(Button, {
              onClick: confirmQuantity,
              disabled: !isQuantityValid || !displayQuantity || isBuying || isTxPending || isSwitchingOrExecuting,
              className: "bg-as-brand hover:bg-as-brand/90 text-as-primary mt-4 h-14 w-full rounded-xl text-lg font-medium",
              children: isBuying || isSwitchingOrExecuting ? "Buying..." : isTxPending ? "Confirming..." : "Continue"
            })]
          }) : null
        })]
      })
    });
  }
  // AnySpend flow for when user needs to swap to B3
  const encodedData = generateEncodedDataForBuyEntriesAndSpin(address || "", userSpinQuantity);
  return _jsx(AnySpendCustom, {
    loadOrder: loadOrder,
    mode: mode,
    recipientAddress: recipientAddress,
    orderType: "custom",
    dstChainId: chainId,
    dstToken: B3_TOKEN,
    dstAmount: totalCost.toString(),
    contractAddress: spinwheelContractAddress,
    spenderAddress: paymentConfig.entryModule,
    encodedData: encodedData,
    metadata: {
      action: `buy ${userSpinQuantity} spin${userSpinQuantity !== "1" ? "s" : ""}`
    },
    header: header,
    onSuccess: txHash => onSuccess?.(txHash),
    showRecipient: false,
    senderAddress: senderAddress,
    slots: slots,
    content: content,
    theme: theme,
    showFiatOption: showFiatOption
  });
}